// Every selector for page markup this autotest reads, mutates or styles lives
// here, and so does every declaration that depends on the page's structure. The
// boundary an arm has is not just DOM mutation: it is mutation, CSS and page
// facts together. (profileFacts.ts holds two read-only presence probes; nothing
// else outside this file names page markup at all.)
//
// An arm file holds none of the page classes named here — by convention, not by
// any check — so any CSS an arm needs that names a page class comes from a
// recipe in this file. A markup change on the profile page is then a fix in this
// file rather than an edit across every arm.
//
// No recipe set pre-covers every future layout. Adding a recipe here is a
// legitimate socketing change; writing a page class in arms/ is not. Read a gap
// below as work owed to this file, never as permission to reach past it.

const SHELL = '.social-profile-container';
const RIGHT_PANEL = '.right-panel-container';
const COLUMN = '.user-content-wrapper';
const UPLOADS = '.uploads-container';
const NAV = '.nav-container';
const NAV_TABLIST = '.nav-tablist';
// Bootstrap responsive-invisibility, so display:none !important. Anything that
// unhides the nav has to carry !important of its own to beat it.
const HIDDEN_XS = '.hidden-xs';

// The identity rail. USER_INFO is the wrapper the Backbone user-info view
// mounts on — "Do not rename this class!" in profile.html.slim — so it IS the
// identity block every seam below means: railTop mounts after it,
// moveIdentityInto moves it, and the band and compaction recipes lay out its
// insides.
const LEFT_PANEL = '.left-panel-container';
const USER_INFO = '.user-info-component-wrapper';
const SUMMARY_CTA = '.user-summary-cta-container';
const SUMMARY = '.user-summary-container';
// The avatar box AND the photo inside it both size off the box's own --size
// custom property (the image is 100%/100% under .profile-user-info), so one
// --size declaration rescales the whole photo.
const AVATAR = '.social-profile-avatar-container';
const CTA_ROW = '.sidebar-cta-container';
const STATS = '.user-stats-container';
const BIO = '.user-bio-container';
const RI_SECTION = '.ri-section';
const EXTERNAL_LINKS = '.external-links-container';

// By data attribute, never by js- class: ProfileBulkFollowModal strips
// js-profile-follow-button off the node when it takes the button over, and the
// attribute is what the page's own click binding keys on anyway. Read under
// CTA_ROW only — the related-authors cards carry data-follow-user-id of their
// own.
const FOLLOW_BUTTON = '[data-follow-user-id]';
const UNFOLLOW_BUTTON = '[data-unfollow-user-id]';

// The one spelling the visible chips and the More menu's items share. The More
// toggle carries role="tab" too — and with an overflow section active it
// repeats that section's count as bare text — so chip readers exclude
// MORE_TOGGLE or they double-count it.
const TAB_LINK = 'a[role="tab"]';
const MORE_TOGGLE = '[data-toggle="dropdown"]';

// One element, two spellings: the pane renders as
// .profile--tab_content_container.js-tab-pane.tab-pane.active. js- classes are
// selected and never styled, so JS asks for the js- one and CSS names the
// presentational one — which has to carry .active regardless, because
// .profile--tab_content_container is display:none until .active turns it back
// on at (0,2,0).
const PANE = '.js-tab-pane';
const PANE_CSS = '.profile--tab_content_container.active';

// Course and video strips carry .js-work-strip too and render into the same
// pane, so the work id is part of the selector: without it they read as cards
// with workId NaN, which poisons a sort comparator and lets an arm chip a video.
const CARD = '.js-work-strip[data-work-id]';
// Every strip in the list, work and video and course alike. A layout recipe
// wants this one — a two-column list has to lay a video out as a cell rather
// than as a full-width band — while anything that READS a work stays on CARD.
const STRIP = '.profile--work_container';
const SECTION_HEADING = '.js-section-heading';

const CARD_TITLE = '.wp-workCard--title';
const CARD_ACTIONS = '.wp-workCard--actions';
const CARD_BODY = '.wp-workCard_itemContainer';
const CARD_ITEM_CLASS = 'wp-workCard_item';
const CARD_ITEM = `.${CARD_ITEM_CLASS}`;
const THUMBNAIL = '.profile--work_thumbnail';
// Duplicated as an id on every card, so it is only ever safe to look up within
// one card's subtree.
const PREMIUM_ROW = '[id="work-strip-premium-row-container"]';
const WORK_LINK = '.js-work-strip-work-link';
// Prefix, not an exact union: autotest CTAs carry a `--<place>` suffix so each gets
// its own click-track source. An exact union would fail silently when someone adds
// an affordance and forgets to list it.
const DOWNLOAD_LINK = '[data-click-track^="profile-work-strip-download"]';
// The stats row's deferred view counter renders as .js-view-count.view-count,
// so CSS names the presentational spelling, per the js- rule above.
const VIEW_COUNT = '.view-count';

const ABSTRACT_TRUNCATED = '.js-work-more-abstract-truncated';
const ABSTRACT_TOGGLE = '.js-work-more-abstract';
const ABSTRACT_FULL = '.js-work-more-abstract-untruncated';

const SLOT_CLASS = 'profile-liautotest-slot';
const CHIPS_CLASS = 'profile-liautotest-chips';
const ACTION_CLASS = 'profile-liautotest-action';
const HEADINGS_HIDDEN = 'data-liautotest-headings-hidden';
const FLAG_PREFIX = 'data-liautotest-';

// Carried by every sheet the autotest injects, base sheets included. Exported
// because testSupport's expectScopedStyles is the only guard against a rule
// escaping an arm's scope, and it cannot find the sheets to check without it.
export const STYLE_CLASS = 'profile-liautotest-style';

// The page's two layout breaks, as tokens rather than as query text. A
// responsive arm needs the SAME break in three places at once — the @media its
// sheet lays the band out in, any rule nested inside that band, and the
// matchMedia its stamp predicts render_state from — and a break that split
// between them would mislabel the column the analysis segments on. narrow and
// wide come off one number, so they can neither overlap nor leave a width in
// neither band.
const COLUMN_BREAK = 992;
const PHONE_BREAK = 768;

export type Breakpoint = 'narrow' | 'phone' | 'wide';

const MEDIA: Record<Breakpoint, string> = {
  narrow: `(max-width: ${COLUMN_BREAK - 1}px)`,
  phone: `(max-width: ${PHONE_BREAK - 1}px)`,
  wide: `(min-width: ${COLUMN_BREAK}px)`,
};

// Whether the viewport is in the band right now. Nothing re-runs this on a
// resize, which is why a responsive arm injects its sheet at every width and
// only its stamp is width-dependent.
export function matchesBreakpoint(band: Breakpoint): boolean {
  return window.matchMedia(MEDIA[band]).matches;
}

// Puts finished rule text — from any recipe here or in armKit — inside a band.
// A general wrapper rather than a `band` option on each recipe: an arm that
// bands one recipe usually bands two, and two @media blocks with the same
// prelude are one block, while a per-recipe option would force it to nest them.
export function mediaCss(band: Breakpoint, css: string): string {
  return `@media ${MEDIA[band]} {\n${css}}\n`;
}

// The autotest's palette. It lives here rather than in armKit because the chips
// base sheet below is the one sheet this file styles itself, and a chip in a
// card has to read as the same system as a row in an armKit panel — and armKit
// already depends on this file for the breakpoints, so pointing the palette the
// same way is the only direction that cannot cycle.
//
// Injected as raw CSS TEXT and never compiled, so a token can only be named as
// var(--token, #fallback) — the form landing.scss and letters/trix.scss use for
// the same reason. The tokens are live here: the application layout renders
// design_system/token_modes/default, putting :root above the shell, so the
// fallback covers only the day a page drops that sheet.
//
// INK is the DS near-black rather than a match for what sits beside it — the
// works list is #333 by inheritance from .DesignSystem and #000 !important on a
// card title, and those two already disagree. That inherited #333 also beats the
// token on any descendant, so a rule wanting INK has to declare it outright
// rather than lean on inheritance, which every emitter here and in armKit does.
//
// FAINT, HAIRLINE, EDGE and PILL were checked and have no token: each falls
// between two neutral steps, and rounding HAIRLINE to --stroke-light (#dddde2)
// would darken every rule the arms draw.
export const INK = 'var(--text-primary, #141413)';
export const MUTED = 'var(--text-secondary, #535366)';
export const FAINT = '#9b9b9b';
export const HAIRLINE = '#f0f0f2';
export const EDGE = '#e5e5e7';
export const PILL = '#f4f4f6';
// The warm shaded-box fill and its own hairline, from the design system's
// background-beige-med (#f2f0ec) and a rule one step deeper. WARM_HAIRLINE
// exists because HAIRLINE was tuned against white and disappears on WARM.
export const WARM = 'var(--background-beige-med, #f2f0ec)';
export const WARM_HAIRLINE = '#e3dfd7';

export type SlotName =
  | 'columnTop'
  | 'listHero'
  | 'listStart'
  | 'railTop'
  | 'pageTop'
  | 'listEnd';

// The four page boxes an arm can be handed a stamp on or write CSS against.
// 'shell' is the default and the one to reach for: it is an ancestor of the
// left rail, the content column, the works list, every card, every slot and
// every panel an arm mounts, so one stamp reaches all of them and no rule has
// to climb back up through :has().
export type SurfacePart = 'shell' | 'column' | 'works' | 'pane';

// 'chips' and 'chip' are this file's own class, not the page's, but an arm may
// no more spell a private class of ours than one of the page's — and four arms
// restyle that row. chip is the span INSIDE the row: the pill lives there, so
// the two are not interchangeable.
export type CardPart =
  | 'row'
  | 'body'
  | 'item'
  | 'title'
  | 'abstract'
  | 'abstractCollapsed'
  | 'thumbnail'
  | 'actions'
  | 'chips'
  | 'chip';

// The revealed abstract and the 100-char teaser are different spans with
// different jobs, and clamping the wrong one either does nothing or empties the
// reveal an arm is built around.
export type AbstractPart = 'abstract' | 'abstractCollapsed';

export type ProfileCard = {
  element: HTMLElement;
  workId: number;
  title: string;
  url: string | null;
  // The download anchor's presence. On the initial cards that mirrors a
  // PROCESSED attachment exactly, which is the +55% signal; the paginated
  // partial renders it for any attachment, so treat it as downloadable rather
  // than as full text.
  hasDownloadable: boolean;
  // The same anchor's href — the page's own per-work download destination, for
  // an arm that re-offers the download as an affordance of its own. null
  // exactly when hasDownloadable is false: both read one anchor, appended
  // cards included.
  downloadUrl: string | null;
  hasHiddenAbstract: boolean;
  // The complete abstract, from the untruncated span when the card has one and
  // from the visible span otherwise (abstracts under 100 chars ship no second
  // span). Paragraph breaks come back as newlines either way. Empty when the
  // work has no abstract at all.
  abstractText(): string;
  // Flips the three sibling classes a real "more" click would flip, leaving the
  // DOM in exactly the state the Backbone handler produces. The handler walks
  // .prev()/.next() positionally, so nothing may ever be inserted between them.
  expandAbstract(): void;
  // Appends as the LAST child of the card item holding the abstract — safely
  // past that three-sibling run, since between them is the one place nothing
  // may land. false for a work with no abstract item at all.
  appendAfterAbstract(node: Node): boolean;
  // The card item chips live in, created on first ask. Handed out because an
  // append-only addChip cannot express a reorder, and two arms have to put a
  // status link after the counts that were added before it.
  chipsRow(): HTMLElement | null;
  // Chips go in their own card item ahead of the actions row — never inside the
  // abstract's three-sibling run.
  addChip(text: string): void;
  prependToTitle(node: Node): void;
  // Hides every part of the card but its title and any chips an arm added.
  // Hides rather than reparents: a .wp-workCard_item outside its container
  // loses its width, its margin and its overflow clipping.
  collapse(): void;
  hideThumbnail(): void;
  // Takes the whole work out of the list without removing it — the paginator
  // counts on the node staying where it is. u-displayNone is the page's own
  // display:none !important, so this survives losing the arm's stylesheet,
  // which is what the three hero arms wanted their collapse() fallback for.
  hide(): void;
  // Puts an action at the head of the card's own actions row, styled by the
  // page. Text goes straight in the anchor: the row's span:first-of-type rule
  // owns the icon gutter, and a lone span in a new action would collect it by
  // accident. collapse() hides that row, so a collapsed card carries a chip
  // rather than an action.
  addAction(label: string, href: string | null): HTMLAnchorElement | null;
  // Stamps data-liautotest-<name> on the card root and hands back the selector
  // that now matches it, on setSurfaceFlag's contract: an arm never spells the
  // attribute itself. The flag sits ON the card, so `${ctx.scope} ${returned}`
  // is the scope a card recipe wants — which is what the recipes' self form
  // below exists for. One value per selector: an arm whose flag carries a
  // per-card value (a rank) and which also wants one rule across all of them
  // needs a second, constant-valued flag for that rule.
  setFlag(name: string, value?: string): string;
  // Reads back what setFlag wrote. Goes through getAttribute rather than
  // dataset on purpose: setAttribute lowercases the name it is given, so a
  // camelCase or multi-word flag does NOT round-trip through the dataset key an
  // author would derive by hand.
  flag(name: string): string | null;
};

function pane(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>(`${PANE}.active`) ??
    document.querySelector<HTMLElement>(PANE)
  );
}

// document.body rather than null for the shell: an arm that loses its scope
// loses its whole stylesheet, and a stamp one box too wide is survivable where
// no stamp at all is not. Nothing else falls back — a rule that missed its box
// is inert, not wrong.
function surfaceElement(part: SurfacePart): HTMLElement | null {
  if (part === 'pane') return pane();
  if (part === 'column') return document.querySelector<HTMLElement>(COLUMN);
  if (part === 'works') return document.querySelector<HTMLElement>(UPLOADS);

  return document.querySelector<HTMLElement>(SHELL) ?? document.body ?? null;
}

function slotAnchor(
  name: SlotName,
): { parent: HTMLElement; before: Node | null } | null {
  if (name === 'columnTop') {
    const column = document.querySelector<HTMLElement>(COLUMN);
    return column && { parent: column, before: column.firstChild };
  }

  if (name === 'railTop') {
    const rail = document.querySelector<HTMLElement>(LEFT_PANEL);
    const identity = rail?.querySelector<HTMLElement>(USER_INFO);
    return rail != null && identity != null
      ? { parent: rail, before: identity.nextSibling }
      : null;
  }

  if (name === 'pageTop') {
    // Straight off SHELL, not surfaceElement('shell'): the body fallback there
    // exists to keep a SCOPE alive, but a slot mounted on the body is a panel
    // rendered outside the page, so no shell means no slot.
    const shell = document.querySelector<HTMLElement>(SHELL);
    return shell && { parent: shell, before: shell.firstChild };
  }

  if (name === 'listEnd') {
    // A SIBLING after the pane, not a last child of it: the paginator appends
    // cards inside the pane, so anything below it stays below every append
    // with no observer needed.
    const list = pane();
    const parent = list?.parentElement;
    return list != null && parent != null
      ? { parent, before: list.nextSibling }
      : null;
  }

  if (name === 'listStart') {
    // The sibling BEFORE the pane — under the "Uploads" header and the section
    // chips, above the first card. A sibling rather than a first child because
    // the one arm that mounts here hides the pane itself while it renders its
    // own list, and a slot inside the pane would vanish with it.
    const list = pane();
    const parent = list?.parentElement;
    return list != null && parent != null ? { parent, before: list } : null;
  }

  const list = pane();
  return list && { parent: list, before: list.firstChild };
}

// The :empty rule is for columnTop and railTop: both anchors are flex
// containers with a gap, and an empty flex item still earns one, so mounting a
// slot and then declining to fill it would be a visible hole rather than the
// control render an arm is promised. The listHero and listEnd anchors take
// their display from plain block containers and spend nothing on an empty
// child, and an empty pageTop grid item generates no row — inert there rather
// than needed.
//
// pageTop sits in the shell's grid, where an unspanned div would be auto-placed
// into the first free track as a one-column cell rather than the band it is
// for. railTop sits in the rail, which is a flex ROW whose only item happens to
// be full width — without its own full width plus a wrap on the rail, a filled
// slot would sit BESIDE the identity card and squeeze it. The wrap is gated on
// the slot through :has() so an unmounted rail keeps its stylesheet
// byte-inert; with the slot mounted but empty it is a no-op, the same way the
// CTA row's own wrap is.
const SLOT_STYLE_ID = 'profile-liautotest-slot-style';
// align-content on the wrapped rail is load-bearing: the rail's grid track is
// as tall as the works column, and a wrapped flex container's default
// align-content of stretch splits that whole height between the two rows —
// which parked a filled railTop slot HALFWAY down the page rather than under
// the identity card.
const SLOT_CSS = `.${SLOT_CLASS}:empty { display: none; }
[data-liautotest-slot="pageTop"] { grid-column: 1 / -1; }
[data-liautotest-slot="railTop"] { width: 100%; }
${LEFT_PANEL}:has(> [data-liautotest-slot="railTop"]) { flex-wrap: wrap; align-content: flex-start; }
`;

// What BASE_CSS in arms/support.ts used to carry, moved here so a chip looks
// like a chip without an arm importing a stylesheet. Deliberately unscoped and
// deliberately thin: four arms restyle this row, and every one of those
// overrides is scoped under the arm's own flag, so it outweighs this sheet
// whatever order the two are injected in.
//
// Typography sits on the ROW and only the pill sits on the span, which is not
// cosmetic ordering: a rule on the span beats one on the row by inheritance
// rather than by weight, so with font-size and line-height down there NO arm
// could reach a chip from the row at any specificity. One arm already shipped
// that bug — ownerFullDose's line-height:1.45 never applied behind BASE_CSS's
// line-height:18px on the span. Chips inherit, so the render is unchanged.
const CHIPS_STYLE_ID = 'profile-liautotest-chips-style';
const CHIPS_CSS = `.${CHIPS_CLASS} {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: ${MUTED};
}
.${CHIPS_CLASS} span {
  background: ${PILL};
  border-radius: 10px;
  padding: 0 8px;
}
`;

// The two sheets this file injects for itself. They carry STYLE_CLASS like an
// arm's own and are DELIBERATELY unscoped, because they style classes only an
// arm's own call can create — so expectScopedStyles has to know them by name or
// it fails every arm that mounts a slot or adds a chip.
export const BASE_STYLE_IDS: readonly string[] = [
  SLOT_STYLE_ID,
  CHIPS_STYLE_ID,
];

// Mounts (once) an empty container the arm owns and returns it. The container
// carries neither .js-work-strip nor data-section, so it cannot corrupt the
// infinite-scroll arming count or the paginator's current-section tracking, and
// it occupies no layout until the arm puts something in it. Both anchors are
// unconditional on this surface, so null means this is not the profile page.
export function mountSlot(name: SlotName): HTMLElement | null {
  const mounted = document.querySelector<HTMLElement>(
    `[data-liautotest-slot="${name}"]`,
  );
  if (mounted != null) return mounted;

  const anchor = slotAnchor(name);
  if (anchor == null) return null;

  injectStyles(SLOT_CSS, SLOT_STYLE_ID);

  const container = document.createElement('div');
  container.className = SLOT_CLASS;
  container.dataset.liautotestSlot = name;
  anchor.parent.insertBefore(container, anchor.before);

  return container;
}

function text(element: Element | null): string {
  return element?.textContent?.trim() ?? '';
}

function hideThumbnailIn(card: HTMLElement): void {
  card.querySelector<HTMLElement>(THUMBNAIL)?.classList.add('u-displayNone');
}

function chipsRowIn(card: HTMLElement): HTMLElement | null {
  const body = card.querySelector<HTMLElement>(CARD_BODY);
  if (body == null) return null;

  const existing = body.querySelector<HTMLElement>(`.${CHIPS_CLASS}`);
  if (existing != null) return existing;

  injectStyles(CHIPS_CSS, CHIPS_STYLE_ID);

  const chips = document.createElement('div');
  chips.className = `${CARD_ITEM_CLASS} ${CHIPS_CLASS}`;
  // insertBefore(null) appends, which is the right answer for a card whose
  // actions row the page declined to render.
  body.insertBefore(chips, body.querySelector(CARD_ACTIONS));

  return chips;
}

// The untruncated span goes through my_simple_format, which turns newlines into
// <br />. textContent drops those entirely, gluing the last word of a paragraph
// to the first of the next — and the truncated span keeps its newlines as text,
// so the accessor would change shape with abstract length.
function textWithBreaks(element: Element | null): string {
  if (element == null) return '';

  const copy = element.cloneNode(true) as HTMLElement;
  copy
    .querySelectorAll('br')
    .forEach((br) => br.replaceWith(document.createTextNode('\n')));

  return copy.textContent?.trim() ?? '';
}

export function readCard(element: HTMLElement): ProfileCard {
  const link = element.querySelector<HTMLAnchorElement>(
    `${CARD_TITLE} ${WORK_LINK}`,
  );
  // The title CELL, not the link inside it: _fast_profile_work_card wraps the title
  // in an anchor only when `work[:indexable] || current_user`, so a logged-out
  // visitor on a non-indexable work gets bare text and the link reads ''. The claim
  // block is a SIBLING of this cell, so its text cannot leak in.
  const titleCell = element.querySelector<HTMLElement>(CARD_TITLE);
  const truncated = element.querySelector<HTMLElement>(ABSTRACT_TRUNCATED);
  const toggle = element.querySelector<HTMLElement>(ABSTRACT_TOGGLE);
  const full = element.querySelector<HTMLElement>(ABSTRACT_FULL);
  const download = element.querySelector<HTMLAnchorElement>(DOWNLOAD_LINK);

  return {
    element,
    workId: Number(element.dataset.workId),
    title: text(link) || text(titleCell),
    url: link?.getAttribute('href') ?? null,
    hasDownloadable: download != null,
    downloadUrl: download?.getAttribute('href') ?? null,
    hasHiddenAbstract: full != null,

    abstractText() {
      return textWithBreaks(full) || text(truncated);
    },

    expandAbstract() {
      if (full == null) return;
      truncated?.classList.add('hidden');
      toggle?.classList.add('hidden');
      full.classList.remove('hidden');
    },

    appendAfterAbstract(node: Node) {
      const item = truncated?.closest<HTMLElement>(CARD_ITEM);
      if (item == null) return false;

      item.appendChild(node);

      return true;
    },

    chipsRow() {
      return chipsRowIn(element);
    },

    addChip(chipText: string) {
      const chip = document.createElement('span');
      chip.textContent = chipText;
      chipsRowIn(element)?.appendChild(chip);
    },

    prependToTitle(node: Node) {
      const title = element.querySelector<HTMLElement>(CARD_TITLE);
      title?.insertBefore(node, title.firstChild);
    },

    collapse() {
      element.querySelectorAll<HTMLElement>(CARD_ITEM).forEach((item) => {
        if (item.matches(CARD_TITLE) || item.matches(`.${CHIPS_CLASS}`)) return;
        item.classList.add('u-displayNone');
      });

      // Empty until the owner-only script fills it, and it carries no card-item
      // class until then, so the loop above never reaches it.
      element
        .querySelector<HTMLElement>(PREMIUM_ROW)
        ?.classList.add('u-displayNone');

      hideThumbnailIn(element);
    },

    hideThumbnail() {
      hideThumbnailIn(element);
    },

    hide() {
      // The whole group, not just the card: the podcast player renders as a
      // sibling, and a player for a work nobody can see is worse than the card.
      cardGroup(element).forEach((node) => node.classList.add('u-displayNone'));
    },

    addAction(label: string, href: string | null) {
      const actions = element.querySelector<HTMLElement>(CARD_ACTIONS);
      if (actions == null) return null;

      const action = document.createElement('a');
      action.className = `wp-workCard--action ${ACTION_CLASS}`;
      action.textContent = label;
      if (href != null) action.href = href;
      actions.insertBefore(action, actions.firstChild);

      return action;
    },

    setFlag(name: string, value = 'true') {
      element.setAttribute(`${FLAG_PREFIX}${name}`, value);

      return flagSelector(name, value);
    },

    flag(name: string) {
      return element.getAttribute(`${FLAG_PREFIX}${name}`);
    },
  };
}

export function readCards(): ProfileCard[] {
  const list = pane();
  if (list == null) return [];

  return Array.from(list.querySelectorAll<HTMLElement>(CARD)).map(readCard);
}

// ---------------------------------------------------------------------------
// The identity rail's Follow CTA. Follow and unfollow are TWO sibling buttons
// swapped by a `hidden` class — the page's binding sits on each node from
// DOMContentLoaded and neither node is ever replaced, so a listener added here
// survives every re-render. What does NOT survive is the follow button's class
// set (the bulk-follow modal strips the js- class) and the unfollow button's
// innerHTML (rewritten wholesale on every hover), which is why the seam keys
// on data attributes and why arms may only insert ADJACENT to the pair.

export type FollowButton = {
  element: HTMLElement;
  insertBeside(node: Node): void;
  onFollowClick(cb: () => void): void;
};

// null on an owner pageview, which renders Upload/Edit in the CTA row and no
// follow node at all — and on any page without the rail.
export function followButton(): FollowButton | null {
  const row = document.querySelector<HTMLElement>(CTA_ROW);
  const element = row?.querySelector<HTMLElement>(FOLLOW_BUTTON);
  if (row == null || element == null) return null;

  return {
    element,

    insertBeside(node: Node) {
      // After the follow/unfollow PAIR — they swap in place, so beside the
      // pair is beside whichever is showing. The CTA row wraps, so a
      // full-width node breaks onto its own line under the buttons.
      (row.querySelector(UNFOLLOW_BUTTON) ?? element).after(node);
    },

    onFollowClick(cb: () => void) {
      element.addEventListener('click', () => {
        // A click is a FOLLOW exactly while the node is showing: `hidden` is
        // the following state at render and after a follow made elsewhere on
        // the page, inline display:none after the button's own post-follow
        // fade. Stale only DURING that ~200ms fade — the same window in which
        // the page's own handler treats a second click as a follow too.
        if (element.classList.contains('hidden')) return;
        if (element.style.display === 'none') return;
        cb();
      });
    },
  };
}

// ---------------------------------------------------------------------------
// The section nav's chips, which carry CORPUS-wide per-section counts
// (section.authorship_count) — unlike the server's @works_count, which reports
// the LIMIT-20 loaded page and must never be read. The "all" chip is included:
// it is a real tab an arm may link to, and it carries no count span, so it
// contributes nothing to worksTotal. The More menu's items count as chips too —
// sections past the visible cap live only there, and a total without them
// undercounts exactly the biggest profiles.

export type SectionChip = {
  name: string;
  count: number | null;
  href: string | null;
};

// The count is the anchor's leading span; the name is everything else. Reading
// the remainder rather than the second span is what keeps one parse working
// for all three chip shapes: the "all" chip has no spans, the visible chips
// wrap the name in a Body span, the More menu's items leave it as bare text.
function readChip(anchor: HTMLAnchorElement): SectionChip {
  const lead = anchor.querySelector(':scope > span');
  const parsed = Number(lead?.textContent?.trim());
  const count = lead != null && Number.isFinite(parsed) ? parsed : null;

  const copy = anchor.cloneNode(true) as HTMLElement;
  if (count != null) copy.querySelector(':scope > span')?.remove();
  const name = copy.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';

  return { name, count, href: anchor.getAttribute('href') };
}

// The nav is .hidden-xs but PRESENT in the DOM, so this reads the same at any
// viewport width.
export function sectionChips(): SectionChip[] {
  const anchors = document.querySelectorAll<HTMLAnchorElement>(
    `${NAV} ${TAB_LINK}:not(${MORE_TOGGLE})`,
  );

  return Array.from(anchors).map(readChip);
}

// The page's own async-filled Mentions stat row: profile.wjs fetches the count
// after DOMContentLoaded and renders this anchor only when its own threshold
// passes, so the anchor's presence is the page's verdict that a public
// mentions page exists for this profile — and its href is the finished URL.
// Null before that fetch lands as much as after it declines, so a reader that
// wants the settled answer re-asks rather than caching the first null.
const MENTIONS_STAT = '.js-mentions-count-container a[href]';

export function mentionsStatUrl(): string | null {
  return (
    document
      .querySelector<HTMLAnchorElement>(MENTIONS_STAT)
      ?.getAttribute('href') ?? null
  );
}

// null rather than 0 when no chip carries a count: a profile whose nav is only
// the "all" chip (or missing outright) has an UNKNOWN corpus size, not an
// empty one.
export function worksTotal(): number | null {
  const counts = sectionChips().flatMap((chip) => chip.count ?? []);
  if (counts.length === 0) return null;

  return counts.reduce((total, count) => total + count, 0);
}

// ---------------------------------------------------------------------------
// The masthead move: the identity block leaves the rail for a band the arm
// mounted (the pageTop slot). appendChild MOVES the node, so the Backbone
// user-info view's bindings, the follow listeners and the deferred stat loads
// all survive the trip. Accepts null so the arm can feed it mountSlot's result
// unchecked.
export function moveIdentityInto(target: HTMLElement | null): boolean {
  const identity = document.querySelector<HTMLElement>(USER_INFO);
  if (target == null || identity == null) return false;

  target.appendChild(identity);

  return true;
}

function hideHeadingsIn(root: HTMLElement): void {
  if (root.matches(SECTION_HEADING)) root.classList.add('u-displayNone');
  root
    .querySelectorAll<HTMLElement>(SECTION_HEADING)
    .forEach((heading) => heading.classList.add('u-displayNone'));
}

// A DOM move is remove-then-insert, so every node reorderCards touches lands in
// the observer's queue as an append. Identity is what tells those apart from a
// real append: a re-entrancy flag cannot, because the callback runs a microtask
// later, after the flag has been reset — and takeRecords() cannot either,
// because it drains the WHOLE queue, so a reorder racing an undelivered
// paginator batch would silently discard that batch.
//
// Seeded at install rather than left empty: the initial onCards pass runs before
// observeAppendedCards, so the server-rendered cards generate no records at all
// and an unseeded set would report every one of them on the first reorder. An
// async onCards resuming after its await is the case that needs the seed.
//
// readCards() reads the ACTIVE pane while the observer watches every pane. That
// is exact rather than sloppy: reorderCards moves nodes in the active pane
// alone, so no other pane can produce a record we would have to recognise.
let deliveredCards = new WeakSet<HTMLElement>();

// The Backbone paginator appends cards straight into the pane and emits no
// event, so the only way to reach them is to watch the DOM. It appends section
// headings the same way, which is why the re-hide lives here too.
export function observeAppendedCards(
  onAppended: (cards: ProfileCard[]) => void,
): void {
  const panes = document.querySelectorAll<HTMLElement>(PANE);

  deliveredCards = new WeakSet(readCards().map((card) => card.element));

  const observer = new MutationObserver((records) => {
    const appended: ProfileCard[] = [];

    for (const record of records) {
      const hidesHeadings = (record.target as HTMLElement).hasAttribute(
        HEADINGS_HIDDEN,
      );

      for (const node of Array.from(record.addedNodes)) {
        if (!(node instanceof HTMLElement)) continue;
        // Ahead of the identity check and never inside it. A reorder's moved
        // cards and a genuinely appended subtree arrive in the SAME batch, and
        // an appended heading is not a card, so gating the re-hide on card
        // identity would leave that heading visible mid-list.
        if (hidesHeadings) hideHeadingsIn(node);

        if (!node.matches(CARD) || deliveredCards.has(node)) continue;
        deliveredCards.add(node);
        appended.push(readCard(node));
      }
    }

    if (appended.length > 0) onAppended(appended);
  });

  panes.forEach((element) => observer.observe(element, { childList: true }));
}

// A card is not the only node a work owns. The paginated partial leaves a
// require_js <script> beside every card, and the podcast promotion renders a
// player container as a sibling rather than a child, so a work is the card plus
// everything up to the next card or heading. Moving the card alone strands them.
function cardGroup(card: HTMLElement): HTMLElement[] {
  const group = [card];

  let sibling = card.nextElementSibling;
  while (
    sibling != null &&
    !sibling.matches(CARD) &&
    !sibling.matches(SECTION_HEADING)
  ) {
    group.push(sibling as HTMLElement);
    sibling = sibling.nextElementSibling;
  }

  return group;
}

// appendChild MOVES a node rather than copying it, so Backbone's per-card
// WorkStripView binding, jQuery .data() and any IntersectionObserver survive.
// Reordering is scoped within each section run by default: cards sit as
// siblings of their section heading, and hoisting one across a heading would
// file it under the wrong one. 'list' scope sorts the whole pane instead, which
// leaves every heading labelling nothing, so it hides them for you.
export function reorderCards(
  compare: (a: ProfileCard, b: ProfileCard) => number,
  scope: 'section' | 'list' = 'section',
): void {
  const list = pane();
  if (list == null) return;

  const runs: HTMLElement[][] = [];
  let run: HTMLElement[] = [];

  for (const child of Array.from(list.children)) {
    if (scope === 'section' && child.matches(SECTION_HEADING)) {
      if (run.length > 0) runs.push(run);
      run = [];
    } else if (child.matches(CARD)) {
      run.push(child as HTMLElement);
    }
  }
  if (run.length > 0) runs.push(run);

  for (const cards of runs) {
    const parent = cards[0].parentNode;
    if (parent == null) continue;

    // Every group is read before anything moves; a move rewrites the sibling
    // chain the next read would walk.
    const groups = new Map(cards.map((card) => [card, cardGroup(card)]));
    const last = groups.get(cards[cards.length - 1]) ?? [];
    const anchor = (last[last.length - 1] ?? cards[cards.length - 1])
      .nextSibling;

    cards
      .map(readCard)
      .sort(compare)
      .forEach((card) =>
        (groups.get(card.element) ?? [card.element]).forEach((node) =>
          parent.insertBefore(node, anchor),
        ),
      );
  }

  if (scope === 'list') hideSectionHeadings();
}

// Hidden, never removed: the paginator reads data-section off these to decide
// when to re-insert a heading for an appended card. It appends those without the
// class, so the pane is flagged and observeAppendedCards hides them on arrival.
export function hideSectionHeadings(): void {
  const list = pane();
  if (list == null) return;

  list.setAttribute(HEADINGS_HIDDEN, 'true');
  hideHeadingsIn(list);
}

// The undo of hideSectionHeadings: the pane flag comes off first so a batch
// arriving mid-restore keeps its headings too. Only headings this file hid are
// revealed — u-displayNone is how it hides them, and nothing else on this page
// puts that class on a heading.
export function showSectionHeadings(): void {
  const list = pane();
  if (list == null) return;

  list.removeAttribute(HEADINGS_HIDDEN);
  if (list.matches(SECTION_HEADING)) list.classList.remove('u-displayNone');
  list
    .querySelectorAll<HTMLElement>(SECTION_HEADING)
    .forEach((heading) => heading.classList.remove('u-displayNone'));
}

// The inverse of a user-clicked reorderCards: capture the pane's child order
// BEFORE the first sort, get back a handle whose restore puts every captured
// node back in that order — headings re-shown. Batches the paginator appends
// after the capture arrive in list order, but a caller that re-sorts them
// destroys that order, so it must call note() BEFORE each re-sort: note folds
// everything currently uncaptured into the snapshot in current DOM order,
// headings included, and restore then replays it. Uncaptured nodes land at the
// tail in whatever order the last sort left them. Both calls are no-ops once
// the reader has navigated to a different pane: those cards were never sorted.
//
// The moves are appendChild, so per reorderCards' own contract every binding
// survives, and observeAppendedCards recognises the nodes as already delivered.
export type ListSnapshot = { note(): void; restore(): void };

export function snapshotListOrder(): ListSnapshot | null {
  const list = pane();
  if (list == null) return null;

  const saved = Array.from(list.children);
  const captured = new Set(saved);

  return {
    note() {
      if (pane() !== list) return;

      Array.from(list.children).forEach((child) => {
        if (captured.has(child)) return;

        captured.add(child);
        saved.push(child);
      });
    },

    restore() {
      if (pane() !== list) return;

      const late = Array.from(list.children).filter(
        (child) => !captured.has(child),
      );

      saved.forEach((node) => {
        if (node.parentNode === list) list.appendChild(node);
      });
      late.forEach((node) => list.appendChild(node));

      showSectionHeadings();
    },
  };
}

// Shared by both flag setters, so the card form and the surface form cannot
// drift. JSON.stringify for the quoting: bucket names and 'true' need none, but
// a hand-rolled `"${value}"` is one stray quote away from an invalid selector,
// which drops the arm's whole rule rather than just failing to match.
function flagSelector(name: string, value: string): string {
  return `[${FLAG_PREFIX}${name}=${JSON.stringify(value)}]`;
}

// Stamps data-liautotest-<name> on a page box and hands back the selector that
// now matches it, so an arm never spells the attribute itself. The default
// target is the shell, which is an ancestor of everything an arm can touch:
// nothing needs :has() to climb back up, and a panel mounted above the works
// list is inside the same scope as the cards below it. null means the box is
// not on this page — for the shell that only happens with no <body> at all.
export function setSurfaceFlag(
  name: string,
  options: { value?: string; target?: SurfacePart } = {},
): string | null {
  const element = surfaceElement(options.target ?? 'shell');
  if (element == null) return null;

  const value = options.value ?? 'true';
  element.setAttribute(`${FLAG_PREFIX}${name}`, value);

  return flagSelector(name, value);
}

// Deduped on the stylesheet TEXT rather than on a per-arm id. Recipes compose,
// so one arm legitimately injects several distinct sheets, and an id-keyed
// guard silently dropped every one after the first. Re-injecting the same text
// — the slot sheet on a second mountSlot, the chips base on a second card — is
// still a no-op.
function injectStyles(css: string, id?: string): void {
  const injected = document.querySelectorAll<HTMLStyleElement>(
    `style.${STYLE_CLASS}`,
  );
  for (const style of Array.from(injected)) {
    if (style.textContent === css) return;
  }

  const style = document.createElement('style');
  style.className = STYLE_CLASS;
  // Only while it is still free: an arm's second sheet arrives under the same
  // arm name, and two elements cannot share an id.
  if (id != null && document.getElementById(id) == null) style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

// An arm carries its own CSS rather than adding to a shared stylesheet, so an
// arm stays one file and teardown stays a deletion.
export function injectArmStyles(arm: string, css: string): void {
  injectStyles(css, `profile-liautotest-style-${arm}`);
}

// ---------------------------------------------------------------------------
// CSS recipes. Each returns finished rule text; the arm owns `within` (from
// ctx.scope, or whatever setSurfaceFlag handed back) and this file owns every
// page class inside it.
//
// A recipe has to work both ways round. `within` is usually an ancestor the
// harness stamped, but an arm that treats only SOME cards scopes to a per-card
// flag, and that flag sits ON the card. Row-level rules are therefore emitted
// twice — as a descendant of `within` and as `within` itself — which costs
// nothing: no element is both a strip and an ancestor of one, so at most one of
// the two can match.

const SURFACE_CSS: Record<SurfacePart, string> = {
  shell: SHELL,
  column: COLUMN,
  works: UPLOADS,
  pane: PANE_CSS,
};

// item, title and actions are all .wp-workCard_item, which the page styles at
// (0,2,0) via `.wp-workCard_itemContainer .wp-workCard_item`. A bare
// `${within} .wp-workCard_item` only ties with that and wins on sheet order, so
// the container is named to break the tie outright.
const CARD_PART_CSS: Record<CardPart, string> = {
  row: STRIP,
  body: CARD_BODY,
  item: `${CARD_BODY} ${CARD_ITEM}`,
  title: `${CARD_BODY} ${CARD_TITLE}`,
  abstract: ABSTRACT_FULL,
  abstractCollapsed: ABSTRACT_TRUNCATED,
  thumbnail: THUMBNAIL,
  actions: `${CARD_BODY} ${CARD_ACTIONS}`,
  chips: `.${CHIPS_CLASS}`,
  chip: `.${CHIPS_CLASS} span`,
};

// The one place rule text is spelled, armKit's emitters included — they narrow
// it to the single-selector case rather than repeating the format.
export function cssRule(selectors: string[], declarations: string): string {
  return `${selectors.join(',\n')} { ${declarations} }\n`;
}

function within(scope: string, selector: string): string[] {
  return [`${scope} ${selector}`];
}

function withinOrSelf(scope: string, selector: string): string[] {
  return [`${scope} ${selector}`, `${scope}${selector}`];
}

export type CompactCardsOptions = {
  // Space between one strip and the next. The list is block flow, so adjacent
  // strip margins still collapse and 5px here is a 5px gap rather than 10 —
  // which stops being true the moment paneGridCss turns the pane into a grid.
  rowGap?: string;
  // The row gap / column gap between items inside one card.
  itemGap?: string;
  // The page already wraps this row, so `true` restates it for legibility and
  // `false` is the only value that changes anything.
  wrap?: boolean;
  titleGrows?: boolean;
};

// One card per line: the item container becomes a baseline-aligned row and the
// items stop claiming full width. Title line-height is deliberately absent —
// the six arms that wanted this disagreed on it, so it belongs in a
// cardPartCss('title', ...) call the arm makes for itself.
export function compactCardsCss(
  scope: string,
  options: CompactCardsOptions = {},
): string {
  const {
    rowGap = '5px',
    itemGap = '0 12px',
    wrap = true,
    titleGrows = true,
  } = options;

  const body = [
    'display: flex;',
    `flex-wrap: ${wrap ? 'wrap' : 'nowrap'};`,
    'align-items: baseline;',
    `gap: ${itemGap};`,
  ].join(' ');

  return [
    cssRule(
      withinOrSelf(scope, STRIP),
      `margin-top: ${rowGap}; margin-bottom: ${rowGap};`,
    ),
    cssRule(within(scope, CARD_PART_CSS.body), body),
    cssRule(
      within(scope, CARD_PART_CSS.item),
      'width: auto; margin-bottom: 0;',
    ),
    cssRule(
      within(scope, CARD_PART_CSS.title),
      `flex: ${titleGrows ? '1 1' : '0 1'} auto;`,
    ),
  ].join('');
}

export type ChipsRowOptions = {
  // Shrink-to-fit beside the title instead of a full-width row under it. The
  // chips row is a .wp-workCard_item, so without this it keeps the item width
  // compactCardsCss gave it.
  inline?: boolean;
  // One unbroken run: neither the row nor the text inside a chip may wrap. Off
  // for the one arm whose "chip" is a sentence.
  nowrap?: boolean;
  gap?: string;
  lineHeight?: string;
  tabularNums?: boolean;
  // The grey pill behind each chip. Every arm that puts the row inline beside
  // a title turns it off — a pill reads as a badge, and a badge beside a title
  // reads louder than the title.
  pill?: boolean;
};

// The chips row is ours, not the page's, so an arm cannot reach it by name.
// Knobs are the values two or more arms disagreed on; a single arm's opinion
// belongs in a cardPartCss('chips' | 'chip', ...) call it makes for itself.
export function chipsRowCss(
  scope: string,
  options: ChipsRowOptions = {},
): string {
  const {
    inline = false,
    nowrap = false,
    gap = '6px',
    lineHeight = '18px',
    tabularNums = false,
    pill = true,
  } = options;

  const row = [
    `gap: ${gap};`,
    `line-height: ${lineHeight};`,
    ...(inline ? ['flex: 0 0 auto;'] : []),
    ...(nowrap ? ['flex-wrap: nowrap;', 'white-space: nowrap;'] : []),
    ...(tabularNums ? ['font-variant-numeric: tabular-nums;'] : []),
  ].join(' ');

  return [
    cssRule(within(scope, CARD_PART_CSS.chips), row),
    // Not a reset of the base sheet but an override of it, because the base is
    // injected by the first chip either way and an arm cannot un-inject it.
    ...(pill
      ? []
      : [
          cssRule(
            within(scope, CARD_PART_CSS.chip),
            'background: none; border-radius: 0; padding: 0;',
          ),
        ]),
  ].join('');
}

// A hairline-separated row instead of a 40px-margined card. This restates the
// strip margin, so an arm using both must inject it AFTER compactCardsCss.
export function collapsedRowCss(
  scope: string,
  options: { rule?: string; padding?: string; margin?: string } = {},
): string {
  const {
    rule = `1px solid ${HAIRLINE}`,
    padding = '5px 0',
    margin = '0',
  } = options;

  return cssRule(
    withinOrSelf(scope, STRIP),
    `margin: ${margin}; padding: ${padding}; border-top: ${rule};`,
  );
}

// Lifts the works list above the feed. Order stays CSS: the feed block hosts
// the site-wide interruption dispatcher, and a DOM move would take its bindings
// with it. Its siblings keep the default order of 0, so one negative order is
// enough. A columnTop slot rides up with it — it is a flex item of the same
// column, and DOM order then puts an arm's panel above the list rather than
// stranding it under the feed. (A listHero slot is inside the pane, which is
// not a flex container, so the same rule is inert there.)
export function worksFirstCss(scope: string): string {
  return cssRule(
    [...withinOrSelf(scope, UPLOADS), `${scope} .${SLOT_CLASS}`],
    'order: -1;',
  );
}

// In the narrow band both panels span the full grid and stack in source order,
// rail first; the panels' own (0,2,0) rules set no order, so one negative order
// on the right panel is enough and the rail needs no rule. This is the one
// recipe that needs the SHELL stamp specifically — the right panel is its child.
// hidden-xs is display:none !important, so the unhide has to be !important too;
// nav-tablist is nowrap with chips budgeted for one desktop row, so on a phone
// it has to be allowed to wrap. An arm reporting render_state for this asks
// matchesBreakpoint('narrow'), which is the same token these two bands come off.
export function stackWorksFirstCss(scope: string): string {
  return [
    mediaCss('narrow', cssRule([`${scope} > ${RIGHT_PANEL}`], 'order: -1;')),
    mediaCss(
      'phone',
      cssRule([`${scope} ${NAV}${HIDDEN_XS}`], 'display: block !important;') +
        cssRule([`${scope} ${NAV} ${NAV_TABLIST}`], 'flex-wrap: wrap;'),
    ),
  ].join('');
}

export function hideRailCss(scope: string): string {
  return cssRule(within(scope, LEFT_PANEL), 'display: none;');
}

// Hiding the rail frees its grid tracks but widens nothing: both panels sit on
// fixed grid-column spans, so the content panel has to be re-spanned across
// the whole row itself. Ties the page's own span rule at (0,2,0) and wins as
// the later sheet, the same tie every recipe here leans on.
export function fullWidthColumnCss(scope: string): string {
  return cssRule(within(scope, RIGHT_PANEL), 'grid-column: 1 / -1;');
}

export type MastheadBandOptions = {
  gap?: string;
  padding?: string;
};

// Lays the MOVED identity block out as a band: photo and name/affiliation
// left, the CTA buttons pushed right, the rest of the block flowing below
// full-width. Every container this touches is already flex in the rail's own
// column layout, so the rules re-direction them; display is restated anyway so
// the recipe stands alone. min-width lets the title shrink and truncate
// instead of shoving the buttons off the row. Emitted unbanded: the arm wraps
// it in mediaCss('wide', ...) beside hideRailCss and fullWidthColumnCss.
export function mastheadBandCss(
  scope: string,
  options: MastheadBandOptions = {},
): string {
  const { gap = '24px', padding = '16px 0' } = options;

  return [
    cssRule(within(scope, USER_INFO), `padding: ${padding};`),
    cssRule(
      within(scope, SUMMARY_CTA),
      'display: flex; flex-direction: row; flex-wrap: wrap; ' +
        `align-items: center; gap: ${gap};`,
    ),
    cssRule(
      within(scope, SUMMARY),
      'display: flex; flex-direction: row; align-items: center; ' +
        `gap: ${gap}; flex: 1 1 auto; min-width: 0;`,
    ),
    cssRule(
      within(scope, CTA_ROW),
      'width: auto; flex: 0 0 auto; margin-left: auto; ' +
        'justify-content: flex-end;',
    ),
  ].join('');
}

export type CompactIdentityOptions = {
  photoSize?: string;
  gap?: string;
  padding?: string;
};

// The identity block as one compact row: small photo, name beside it, the
// secondary rail content gone. The CTA row is deliberately untouched — Follow
// and Message keep their full-size 36px targets under everything this recipe
// compacts, on their own full-width line below the row. Emitted unbanded; the
// phone arm bands it under 'narrow'.
export function compactIdentityCss(
  scope: string,
  options: CompactIdentityOptions = {},
): string {
  const { photoSize = '48px', gap = '12px', padding = '12px 16px' } = options;

  return [
    cssRule(within(scope, USER_INFO), `gap: ${gap}; padding: ${padding};`),
    cssRule(within(scope, SUMMARY_CTA), `gap: ${gap};`),
    cssRule(
      within(scope, SUMMARY),
      `flex-direction: row; align-items: center; gap: ${gap};`,
    ),
    // One declaration, whole photo: box and image both size off --size.
    cssRule(within(scope, AVATAR), `--size: ${photoSize};`),
    cssRule(
      [
        ...within(scope, STATS),
        ...within(scope, BIO),
        ...within(scope, RI_SECTION),
        ...within(scope, EXTERNAL_LINKS),
      ],
      'display: none;',
    ),
  ].join('');
}

// The pane's display comes from a (0,2,0) rule and grid stops the strips' own
// margins collapsing, so the scope is what buys the weight and the margin
// becomes gap. Everything spans every column and only the strips are un-spanned,
// so an unknown future child cannot land half width.
//
// Banded rather than unconditional, and the band is a token rather than a
// length: a two-column grid at phone width is not a thinner layout but a broken
// one, and an arm gating its DOM edits on the same layout has to be able to ask
// matchesBreakpoint for exactly this band.
export function paneGridCss(
  scope: string,
  options: { columns?: number; gap?: string; band?: Breakpoint } = {},
): string {
  const { columns = 2, gap = '16px 32px', band = 'wide' } = options;
  const grid = `${scope} ${PANE_CSS}`;

  return mediaCss(
    band,
    cssRule(
      [grid],
      `display: grid; grid-template-columns: repeat(${columns}, 1fr); ` +
        `gap: ${gap}; align-items: start;`,
    ) +
      cssRule([`${grid} > *`], 'grid-column: 1 / -1;') +
      cssRule([`${grid} > ${STRIP}`], 'grid-column: auto; margin: 0;'),
  );
}

// All four declarations are one mechanism: drop display or box-orient and the
// clamp silently stops clamping rather than failing loudly. Defaults to the
// revealed abstract; pass 'abstractCollapsed' to shorten the 100-char teaser
// instead, which is the only correct target for an arm that reveals on scroll.
// Emitted unbanded: an arm clamping only inside a layout band wraps this in
// mediaCss with the band its layout recipe was given.
export function clampAbstractCss(
  scope: string,
  lines: number,
  part: AbstractPart = 'abstract',
): string {
  return cssRule(
    within(scope, CARD_PART_CSS[part]),
    'display: -webkit-box; -webkit-box-orient: vertical; ' +
      `-webkit-line-clamp: ${lines}; overflow: hidden;`,
  );
}

// The CSS twin of expandAbstract: the same three spans flipped by rule rather
// than by class, so an arm revealing inside a band goes back to control the
// moment the viewport leaves it. .hidden is display:none !important, so the
// reveal has to be !important too — and it has to reveal AS -webkit-box,
// because the clamp lands on that same span and `block` would out-weigh it and
// silently stop the clamping. The teaser goes only where the toggle sits beside
// it: an abstract under 100 characters ships neither, so hiding it there would
// take the only copy of the abstract off the card.
export function revealAbstractCss(scope: string, lines: number): string {
  return [
    cssRule(
      [
        ...within(scope, `${ABSTRACT_TRUNCATED}:has(+ ${ABSTRACT_TOGGLE})`),
        ...within(scope, ABSTRACT_TOGGLE),
      ],
      'display: none;',
    ),
    cssRule(within(scope, ABSTRACT_FULL), 'display: -webkit-box !important;'),
    clampAbstractCss(scope, lines),
  ].join('');
}

// All three abstract siblings at once, the toggle included — it has no
// CardPart entry on purpose, since a rule aimed at it alone invites re-flowing
// a run nothing may be inserted into, so it is wired here rather than exposed.
// Hiding by rule leaves the DOM exactly as the Backbone "more" handler expects
// it, and an arm that suppresses within a band goes back to control the moment
// the viewport leaves it. Pair with ProfileCard#appendAfterAbstract for a
// replacement teaser.
export function suppressAbstractCss(scope: string): string {
  return cssRule(
    [
      ...within(scope, ABSTRACT_TRUNCATED),
      ...within(scope, ABSTRACT_TOGGLE),
      ...within(scope, ABSTRACT_FULL),
    ],
    'display: none;',
  );
}

// The figures slot renders as an empty card item on the 94.5% of works with no
// figures, and still spends its 1em. :empty is inert rather than wrong if Slim
// ever emits whitespace into it.
export function hideEmptyCardItemsCss(scope: string): string {
  return cssRule(
    within(scope, `${CARD_PART_CSS.item}:empty`),
    'display: none;',
  );
}

// The page's own per-card view counter, for an arm whose chip restates it.
// Scope it to the per-card flag composed under ctx.scope, so a card the arm
// left unchipped keeps the page's count.
export function hideViewCountCss(scope: string): string {
  return cssRule(within(scope, VIEW_COUNT), 'display: none;');
}

// Escape hatches: this file owns WHICH element, the arm owns WHICH declarations.
// Reach for a named recipe first — these two carry no invariant of their own,
// so a page change can only be chased through the arms that used them.
export function cardPartCss(
  part: CardPart,
  scope: string,
  declarations: string,
): string {
  const selector = CARD_PART_CSS[part];
  const selectors =
    part === 'row' ? withinOrSelf(scope, selector) : within(scope, selector);

  return cssRule(selectors, declarations);
}

export function surfacePartCss(
  part: SurfacePart,
  scope: string,
  declarations: string,
): string {
  return cssRule(withinOrSelf(scope, SURFACE_CSS[part]), declarations);
}
